import React from 'react'
import { ControlledModal } from '../../layouts/components/modal'
import { Guardian, Student, Teacher, useAppContext, User } from '../../providers/context'
import { makeRequest, showAxiosErrorAsToast } from '../../request'
import { Button } from '../../components/simple/button'
import { useTranslation } from '../../providers/translation'
import { TextInput } from '../../components/form/textInput'
import assert from 'assert'
import { EditResponse, showEditErrors } from './editUser'
import { Popups } from '../../providers/popups'
import { useShowWithPriority } from '../../providers/modalPriorityProvider'

const useSharedUpdatedContactsMethods = (user: User) => {
  const { reloadUser, setState } = useAppContext()
  const requestedContactMethods = user.requestUpdatedContacts!
  assert(requestedContactMethods, 'User does not have requested contact methods')

  const { active, onComplete } = useShowWithPriority(800)
  const onClose = (reloadUserAfter: boolean = false) => {
    onComplete()
    setState((s) => s && { ...s, user: s.user && { ...s.user, requestUpdatedContacts: undefined } })
    makeRequest('/frontend_api/markOnboardingComplete', { onboarding: 'updateContactsModal' })
      .catch(showAxiosErrorAsToast)
      .then(() => {
        if (reloadUserAfter) reloadUser()
      })
  }
  return { active, onClose, requestedContactMethods }
}

export const UpdateContactMethodsFamilyModal: React.FC<{ user: Student | Guardian }> = ({ user }) => {
  const { active, onClose, requestedContactMethods } = useSharedUpdatedContactsMethods(user)
  const { tr } = useTranslation()
  const [loading, setLoading] = React.useState(false)
  assert(
    !requestedContactMethods.email || user.type === 'guardian',
    'Only guardians can have email requested to change'
  )

  const [email, setEmail] = React.useState(requestedContactMethods.email || '')
  const [emailInvalid, setEmailInvalid] = React.useState<boolean>(false)
  const [phone, setPhone] = React.useState(requestedContactMethods.phoneNumber)
  const [phoneInvalid, setPhoneInvalid] = React.useState<boolean>(false)

  return (
    <ControlledModal
      size="xs"
      onOpenChange={(o) => {
        if (!o) onClose()
      }}
      open={active}
      title={tr('Help us reach you!')}
      disabled={loading}
    >
      <div>
        {tr(
          `Please enter your phone number ${requestedContactMethods.email !== undefined ? 'and email ' : ''}so that teachers can contact you.`
        )}
      </div>
      {requestedContactMethods.email !== undefined && (
        <div className="flex items-center gap-2">
          <b>{tr('Email Address')}</b>
          <TextInput value={email} onChange={setEmail} invalid={!!emailInvalid} blue validation="email" />
        </div>
      )}
      <div className="flex items-center gap-2">
        <b>{tr('Phone Number')}</b>
        <TextInput value={phone} onChange={setPhone} invalid={!!phoneInvalid} blue validation="phone" />
      </div>
      <div className="text-sm text-gray-600">
        {tr('This phone number will need to be validated by clicking a link we text you')}
      </div>
      <div className="mt-3 flex justify-between">
        <Button
          logo
          disabled={loading}
          onClick={() => {
            setEmailInvalid(false)
            setPhoneInvalid(false)
            setLoading(true)
            makeRequest<EditResponse>('/frontend_api/editUser', {
              phoneNumber: phone.trim(),
              email: requestedContactMethods.email === undefined ? undefined : email.trim(),
            })
              .then((r) => {
                if (r == 'success') {
                  if (
                    user.type === 'guardian' &&
                    (email !== requestedContactMethods.email || phone !== requestedContactMethods.phoneNumber)
                  )
                    Popups.confirm({
                      title: tr('Thank you for updating your contact information!'),
                      message: tr(
                        'Please notify the school about any changes to ensure you receive all important communications.'
                      ),
                      yesText: tr('OK'),
                    })
                  onClose(true)
                } else showEditErrors(r, setPhoneInvalid, setEmailInvalid, tr)
              })
              .catch(showAxiosErrorAsToast)
              .finally(() => setLoading(false))
          }}
        >
          {tr('Save')}
        </Button>
        <Button disabled={loading} onClick={() => onClose(false)}>
          {tr('No Thanks')}
        </Button>
      </div>
    </ControlledModal>
  )
}
export const UpdateContactMethodsTeacherModal: React.FC<{ user: Teacher }> = ({ user }) => {
  const { active, onClose, requestedContactMethods } = useSharedUpdatedContactsMethods(user)
  const { tr } = useTranslation()
  assert(!requestedContactMethods.email, 'Teachers cannot have email requested to change')
  const [loading, setLoading] = React.useState(false)

  const [phone, setPhone] = React.useState(requestedContactMethods.phoneNumber)
  const [phoneInvalid, setPhoneInvalid] = React.useState<boolean>(false)

  return (
    <ControlledModal
      size="xs"
      onOpenChange={(o) => {
        if (!o) onClose()
      }}
      open={active}
      title={tr('Help us reach you!')}
      disabled={loading}
    >
      <div>
        Please enter your phone number so that the district can reach you. Your phone number is only for alerts and will
        not be visible to students or families.
      </div>
      <div className="flex items-center gap-2">
        <b>{tr('Phone Number')}</b>
        <TextInput value={phone} onChange={setPhone} invalid={phoneInvalid} blue validation="email" />
      </div>
      <div className="mt-3 flex justify-between">
        <Button
          logo
          disabled={loading}
          onClick={() => {
            setLoading(true)
            makeRequest<EditResponse>('/frontend_api/editUser', { phoneNumber: phone })
              .then((r) => {
                if (r == 'success') {
                  onClose(true)
                } else showEditErrors(r, setPhoneInvalid)
              })
              .catch(showAxiosErrorAsToast)
              .finally(() => setLoading(false))
          }}
        >
          {tr('Save')}
        </Button>
        <Button disabled={loading} onClick={() => onClose(false)}>
          {tr('No Thanks')}
        </Button>
      </div>
    </ControlledModal>
  )
}
